import { IBuilderMarketingBannerData } from './composed/IBuilderMarketingBannerData';
import { IBuilderNavigationData } from './composed/IBuilderNavigationData';
import { DataModelTarget } from '../schemas';
import type { IBuilderCategoryData } from './composed/IBuilderCategoryData';
import type { IBuilderPageData } from './composed/IBuilderPageData';
import type { IBuilderDataModelResponse } from './base/IBuilderDataModelResponse';
import type { IBuilderProductData } from './composed/IBuilderProductData';
import type { IBuilderPromotionMessageData } from './composed/IBuilderPromotionMessageData';

/**
 * A map of Builder data model targets to their corresponding content interfaces.
 */
interface IBuilderDataModelContentMap {
  /**
   * The navigation content model.
   */
  [DataModelTarget.NavigationContentModel]: IBuilderNavigationData;
  /**
   * The marketing banner content model.
   */
  [DataModelTarget.MarketingBannerModel]: IBuilderMarketingBannerData;
  /**
   * The category content model.
   */
  [DataModelTarget.CategoryModel]: IBuilderCategoryData;
  /**
   * The product content model used for the PDP content.
   */
  [DataModelTarget.ProductModel]: IBuilderProductData;
  /**
   * The page content model.
   */
  [DataModelTarget.PageModel]: IBuilderPageData;
  /**
   * The promotion message content model.
   */
  [DataModelTarget.PromotionMessageModel]: IBuilderPromotionMessageData;
}

/**
 * The content model for builder.io content mapped to a specific data model enum.
 */
export type BuilderDataModelContentFromTarget<T extends DataModelTarget> =
  IBuilderDataModelResponse<IBuilderDataModelContentMap[T]>;

/**
 * Checks if the given target is a valid Builder data model target.
 * @param target - The target to check.
 * @returns True if the target is a valid Builder data model target, false otherwise.
 */
export function isBuilderDataModelTarget(
  target: string | undefined
): target is DataModelTarget {
  if (target === undefined) return false;
  return Object.values(DataModelTarget).includes(target as DataModelTarget);
}

export {
  type IBuilderNavigationData,
  type IBuilderMarketingBannerData,
  type IBuilderPromotionMessageData
};
